import { Calendar, ChartNoAxesColumn, Network, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ActionMenu } from "../../components/ui/action-menu";
import { Badge } from "../../components/ui/badge";
import { formatDuration } from "../../lib/time";
import { cn } from "../../lib/utils";
import type { AgentAnalytics, Workflow } from "./types";
import { getWorkflowId, getWorkflowPaths } from "./card-routes";

const health = (rate: number) =>
  rate >= 90 ? "var(--color-success)" : rate >= 60 ? "var(--color-warning)" : "var(--color-danger)";

export function WorkflowCard({
  workflow,
  stats,
  canEdit,
  onTestCall,
}: {
  workflow: Workflow;
  stats?: AgentAnalytics;
  canEdit: boolean;
  onTestCall: (workflow: Workflow) => void;
}) {
  const navigate = useNavigate();
  const workflowId = getWorkflowId(workflow);
  const workflowName = workflow.name || workflow.agent_name || "Untitled Workflow";
  const displayWorkflowName = workflowName.length > 50 ? workflowName.slice(0, 50) + "..." : workflowName;
  // Lifetime counter off the workflow document. `stats` is analytics, which
  // defaults to a 7-day window -- preferring it here understated the total for
  // any workflow whose traffic predates the window.
  const calls = workflow.total_calls ?? stats?.total_calls ?? 0;
  // Same figure the Analytics page shows as "Connectivity": unique contacts
  // reached / unique contacts attempted. Dividing connected_calls by total_calls
  // instead counted every retry of one contact as its own miss, which is why
  // this read far lower than the agent's real connectivity.
  const windowCalls = stats?.total_calls || 0;
  const successRate = stats?.unique_connectivity_percentage != null
    ? Math.round(stats.unique_connectivity_percentage)
    : windowCalls > 0 && stats
      ? Math.round(((stats.unique_reached ?? stats.connected_calls ?? 0) / (stats.unique_contacts || windowCalls || 1)) * 100)
      : 0;
  const paths = getWorkflowPaths(workflow);

  return (
    <div
      onClick={() => canEdit && navigate(paths.edit)}
      className={cn(
        "agent-card group flex flex-col p-4 transition-all hover:-translate-y-0.5 hover:shadow-card-hover shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524]",
        canEdit ? "cursor-pointer" : "cursor-default",
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400 font-semibold text-sm">
          <Network className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-start gap-2">
            <p className="line-clamp-2 break-words text-sm font-semibold text-ink" title={workflowName}>{displayWorkflowName}</p>
            <Badge tone="violet" className="shrink-0 mt-0.5">Multi-agent</Badge>
          </div>
          <span className="mt-0.5 inline-flex rounded bg-soft px-1.5 py-0.5 font-mono text-[9px] text-ink-muted">
            {workflow.version_name || workflow.version_id || "Workflow version"}
          </span>
        </div>
        <div onClick={(event) => event.stopPropagation()}>
          <ActionMenu
            ariaLabel={`${workflowName} actions`}
            items={[
              { label: "Test Call", onClick: () => onTestCall(workflow) },
              { label: "Schedule", onClick: () => navigate(paths.schedule) },
              { label: "Analytics", onClick: () => navigate(paths.analytics) },
              ...(canEdit ? [{ label: "Edit Workflow", onClick: () => navigate(paths.edit) }] : []),
            ]}
          />
        </div>
      </div>

      <p className="mt-2.5 line-clamp-2 min-h-[2.5rem] text-sm text-ink-muted">
        {workflow.description || " "}
      </p>

      <div className="my-2.5 grid grid-cols-3 divide-x divide-line border-y border-line py-2">
        {[
          { value: calls.toLocaleString("en-IN"), label: "CALLS" },
          { value: `${successRate}%`, label: "SUCCESS", color: health(successRate) },
          { value: formatDuration(stats?.avg_duration_seconds || 0), label: "AVG LEN" },
        ].map((item) => (
          <div key={item.label} className="px-2 first:pl-0">
            <p className="font-mono text-sm font-semibold tabular-nums" style={{ color: item.color ?? "var(--color-ink)" }}>{item.value}</p>
            <p className="font-mono text-[9px] uppercase tracking-wide text-ink-faint">{item.label}</p>
          </div>
        ))}
      </div>

      <div className="h-1 overflow-hidden rounded-full bg-line">
        <div className="h-full rounded-full" style={{ width: `${successRate}%`, background: health(successRate) }} />
      </div>

      <div className="mt-3 flex items-center gap-1.5 border-t border-line pt-2.5" onClick={(event) => event.stopPropagation()}>
        <button onClick={() => onTestCall(workflow)} className="flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100/80 border border-emerald-200/60 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20 px-2.5 py-1 text-xs font-medium transition-colors">
          <Phone className="h-3 w-3" /> Test Call
        </button>
        <button
          onClick={() => navigate(paths.schedule)}
          className="flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-ink shadow-[0_0_0_1px_rgba(28,25,23,0.08)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08)] dark:text-stone-100 hover:bg-accent-50 hover:text-accent-700 dark:hover:bg-accent-500/10 dark:hover:text-accent-400 transition-colors"
        >
          <Calendar className="h-3 w-3" /> Schedule
        </button>
        <button aria-label="Analytics" onClick={() => navigate(paths.analytics)} className="flex h-6 w-6 items-center justify-center rounded-md bg-soft text-ink-muted hover:bg-line hover:text-ink transition-colors">
          <ChartNoAxesColumn className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
